import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import StopIcon from '@mui/icons-material/Stop'
import AlarmIcon from '@mui/icons-material/Alarm'
import { useEffect, useRef, useState } from 'react'

export default function Timer() {
  const [minutes, setMinutes] = useState<number | ''>('')
  const [seconds, setSeconds] = useState<number | ''>('')
  const [remaining, setRemaining] = useState<number | null>(null)
  const [running, setRunning] = useState(false)
  const [alertOpen, setAlertOpen] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (running && remaining !== null) {
      intervalRef.current = setInterval(() => {
        setRemaining((prev) => {
          if (prev === null || prev <= 1) {
            clearInterval(intervalRef.current!)
            setRunning(false)
            setAlertOpen(true)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [running])

  const totalSeconds = (Number(minutes) || 0) * 60 + (Number(seconds) || 0)

  const handleStart = () => {
    if (totalSeconds <= 0) {
      return
    }
    setRemaining(totalSeconds)
    setRunning(true)
  }

  const handleStop = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }
    setRunning(false)
    setRemaining(null)
  }

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60)
    const sec = s % 60
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
  }

  return (
    <>
      <Box className="flex items-center gap-3">
        <AlarmIcon color="action" />
        {running && remaining !== null ? (
          <>
            <Typography variant="h6" fontWeight="bold" sx={{ minWidth: 64, fontVariantNumeric: 'tabular-nums' }}>
              {formatTime(remaining)}
            </Typography>
            <IconButton size="small" color="error" onClick={handleStop} title="Arrêter">
              <StopIcon />
            </IconButton>
          </>
        ) : (
          <>
            <Box display="flex" flexDirection="row" alignItems="center" gap={0.5}>
              <TextField
                label="min"
                type="number"
                size="small"
                value={minutes}
                onChange={(e) => setMinutes(e.target.value === '' ? '' : Math.max(0, Number(e.target.value)))}
                onKeyDown={(e) => ['-', '+', '.', ',', 'e'].includes(e.key) && e.preventDefault()}
                inputProps={{ min: 0, max: 99, step: 1 }}
                sx={{ width: 72 }}
              />
              <Typography variant="body2" color="text.secondary">:</Typography>
              <TextField
                label="sec"
                type="number"
                size="small"
                value={seconds}
                onChange={(e) => setSeconds(e.target.value === '' ? '' : Math.min(59, Math.max(0, Number(e.target.value))))}
                onKeyDown={(e) => ['-', '+', '.', ',', 'e'].includes(e.key) && e.preventDefault()}
                inputProps={{ min: 0, max: 59, step: 1 }}
                sx={{ width: 72 }}
              />
            </Box>
            <Button
              variant="contained"
              size="small"
              startIcon={<PlayArrowIcon />}
              onClick={handleStart}
              disabled={totalSeconds <= 0}
            >
              Lancer
            </Button>
          </>
        )}
      </Box>

      <Dialog
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 100 }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AlarmIcon color="warning" />
          Alerte minuteur
        </DialogTitle>
        <DialogContent>
          <Typography>Le compte à rebours est terminé.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlertOpen(false)} variant="contained" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
